import React, { useRef } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import { get } from 'lodash';
import { LoadingButton, Page } from 'src/components';
import { useSnackBar } from 'src/hooks';
import useUserInfo from 'src/hooks/user/use-user-info';
import { SupabaseClientService } from 'src/services';
import {
  addTicketComment,
  taptapCreateTicketLogs
} from 'src/redux/slices/taptap-customer';
import { taptapCustomerField } from 'src/redux/slices/taptap-customer/constants/taptap-fields';
import { taptapOptionsBranchReleased } from 'src/redux/slices/taptap-customer/constants/taptap-branch-released';
import { taptapOptionsCouriers } from 'src/redux/slices/taptap-customer/constants/taptap-courier';
import {
  taptapOptionsSalesTransferType,
  taptapServiceTypesWithSalesTransferBranch
} from 'src/redux/slices/taptap-customer/constants/taptap-form-fields';
import { taptapStatuses } from 'src/redux/slices/taptap-customer/constants/taptap-flow-process';
import { taptapSource } from 'src/redux/slices/taptap-customer/constants/taptap-source';

type SalesTransferRequestFormValues = {
  first_name: string;
  last_name: string;
  contact_no: string;
  address: string;
  branch_released: string;
  courier: string;
  service_type: string;
  kind_of_sales_transfer: string;
  requested_items: string;
  remarks: string;
};

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  card: {
    borderRadius: 8
  }
}));

const initialValues: SalesTransferRequestFormValues = {
  first_name: '',
  last_name: '',
  contact_no: '',
  address: '',
  branch_released: '',
  courier: '',
  service_type: '',
  kind_of_sales_transfer: '',
  requested_items: '',
  remarks: ''
};

const formSchema = yup.object().shape({
  first_name: yup.string().required('First name is required'),
  last_name: yup.string().required('Last name is required'),
  contact_no: yup
    .string()
    .nullable()
    .matches(/\d+$/, 'Contact number must be numeric'),
  address: yup.string().required('Address is required'),
  branch_released: yup.string().required('Requestor branch is required'),
  courier: yup.string().required('Courier is required'),
  service_type: yup
    .string()
    .required('Sales transfer service type is required'),
  kind_of_sales_transfer: yup
    .string()
    .required('Kind of sales transfer is required'),
  requested_items: yup.string().required('Requested items are required')
});

const buildRequestComment = (values: SalesTransferRequestFormValues) =>
  [
    `Sales Transfer Request - ${values.kind_of_sales_transfer}`,
    '',
    'Requested item(s):',
    values.requested_items,
    values.remarks ? ['', 'Remarks:', values.remarks] : []
  ]
    .flat()
    .join('\n');

const AddSalesTransferRequest = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const snackBar = useSnackBar();
  const { userDetails } = useUserInfo();
  const isLoading = useRef(false);

  const formik = useFormik({
    initialValues,
    validationSchema: formSchema,
    onSubmit: async (values) => {
      if (isLoading.current) {
        return;
      }

      isLoading.current = true;

      const customerPayload = {
        [taptapCustomerField.FIRST_NAME]: values.first_name,
        [taptapCustomerField.LAST_NAME]: values.last_name,
        [taptapCustomerField.CONTACT_NO]: values.contact_no,
        [taptapCustomerField.ADDRESS]: values.address,
        [taptapCustomerField.BRANCH_RELEASED]: values.branch_released,
        [taptapCustomerField.COURIER]: values.courier,
        [taptapCustomerField.SERVICE_TYPE]: values.service_type,
        [taptapCustomerField.KIND_OF_SALES_TRANSFER]:
          values.kind_of_sales_transfer,
        [taptapCustomerField.REMARKS]: values.remarks || null,
        [taptapCustomerField.STATUS]: taptapStatuses.PENDING_SALES_TRANSFER,
        [taptapCustomerField.SOURCE]: taptapSource.WALK_IN,
        [taptapCustomerField.TOTAL_AMOUNT]: 0,
        [taptapCustomerField.BALANCE]: 0,
        paid_amount: 0,
        quotation: [],
        shipping_fee: 0,
        is_shipping_fee_paid: false,
        is_agree_tnc: true,
        is_agree_marketing: false,
        is_deleted: false
      };

      const { data, error } = await SupabaseClientService.from('customers')
        .insert(customerPayload)
        .select('id');

      if (error || !data?.[0]?.id) {
        snackBar.show({
          severity: 'error',
          message: JSON.stringify(error || 'Failed to create request')
        });
        isLoading.current = false;
        return;
      }

      const ticketId = data[0].id;
      const fullName =
        `${userDetails?.first_name || ''} ${userDetails?.last_name ||
          ''}`.trim() || 'System';

      await taptapCreateTicketLogs({
        ticket_id: ticketId,
        status: taptapStatuses.PENDING_SALES_TRANSFER,
        updated_by: fullName
      });

      const { error: commentError } = await addTicketComment({
        customer_id: ticketId,
        full_name: 'System',
        comment: buildRequestComment(values),
        tag: 'SALES_TRANSFER_REQUEST'
      });

      if (commentError) {
        snackBar.show({
          severity: 'warning',
          message: 'Request created, but requested items comment failed.'
        });
      } else {
        snackBar.show({
          severity: 'success',
          message: 'Sales transfer request created successfully'
        });
      }

      isLoading.current = false;
      navigate(`/app/customer-taptap/${ticketId}`);
    }
  });

  const getFieldError = (field: string) =>
    Boolean(get(formik.touched, field) && get(formik.errors, field));

  const getHelperText = (field: string) =>
    get(formik.touched, field) && get(formik.errors, field)
      ? get(formik.errors, field)
      : undefined;

  return (
    <Page className={classes.root} title="Sales Transfer Request">
      <Container maxWidth="md">
        <Box mb={2}>
          <Typography color="textPrimary" variant="h3">
            Sales Transfer Request
          </Typography>
        </Box>

        <Card className={classes.card} elevation={3}>
          <CardHeader title="Request Details" />
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  onChange={formik.handleChange}
                  value={formik.values.first_name}
                  variant="outlined"
                  error={getFieldError('first_name')}
                  helperText={getHelperText('first_name')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  onChange={formik.handleChange}
                  value={formik.values.last_name}
                  variant="outlined"
                  error={getFieldError('last_name')}
                  helperText={getHelperText('last_name')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact No."
                  name="contact_no"
                  onChange={formik.handleChange}
                  value={formik.values.contact_no}
                  variant="outlined"
                  error={getFieldError('contact_no')}
                  helperText={getHelperText('contact_no')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Courier"
                  name="courier"
                  onChange={formik.handleChange}
                  select
                  value={formik.values.courier}
                  variant="outlined"
                  error={getFieldError('courier')}
                  helperText={getHelperText('courier')}
                >
                  {taptapOptionsCouriers.map((option) => (
                    <MenuItem key={option.key} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  onChange={formik.handleChange}
                  value={formik.values.address}
                  variant="outlined"
                  error={getFieldError('address')}
                  helperText={getHelperText('address')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Requestor Branch"
                  name="branch_released"
                  onChange={formik.handleChange}
                  select
                  value={formik.values.branch_released}
                  variant="outlined"
                  error={getFieldError('branch_released')}
                  helperText={getHelperText('branch_released')}
                >
                  {taptapOptionsBranchReleased.map((option) => (
                    <MenuItem key={option.key} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Service Type"
                  name="service_type"
                  onChange={formik.handleChange}
                  select
                  value={formik.values.service_type}
                  variant="outlined"
                  error={getFieldError('service_type')}
                  helperText={getHelperText('service_type')}
                >
                  {taptapServiceTypesWithSalesTransferBranch.map(
                    (serviceType) => (
                      <MenuItem key={serviceType} value={serviceType}>
                        {serviceType}
                      </MenuItem>
                    )
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Kind of Sales Transfer"
                  name="kind_of_sales_transfer"
                  onChange={formik.handleChange}
                  select
                  value={formik.values.kind_of_sales_transfer}
                  variant="outlined"
                  error={getFieldError('kind_of_sales_transfer')}
                  helperText={getHelperText('kind_of_sales_transfer')}
                >
                  {taptapOptionsSalesTransferType.map((option) => (
                    <MenuItem key={option.key} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Requested Item(s)"
                  name="requested_items"
                  onChange={formik.handleChange}
                  multiline
                  minRows={5}
                  value={formik.values.requested_items}
                  variant="outlined"
                  error={getFieldError('requested_items')}
                  helperText={getHelperText('requested_items')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks"
                  name="remarks"
                  onChange={formik.handleChange}
                  multiline
                  minRows={3}
                  value={formik.values.remarks}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <LoadingButton
                  color="primary"
                  fullWidth
                  height={48}
                  loading={isLoading.current}
                  onClick={() => formik.handleSubmit()}
                  title="Create Sales Transfer Request"
                  variant="contained"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default AddSalesTransferRequest;
